import { useState } from 'react';
import { Check, KeyRound, Lock, LogOut, ShieldCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import PageHeader from './PageHeader';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Field, FieldDescription, FieldGroup, FieldLabel } from './ui/field';
import { Input } from './ui/input';

const ChangePassword = () => {
  const { updatePassword, logout } = useAuth();
  const navigate = useNavigate();
  const [passwords, setPasswords] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [isLoading, setIsLoading] = useState(false);

  const validatePasswords = () => {
    if (passwords.newPassword === passwords.currentPassword) return toast.error('新密码不能与当前密码相同'), false;
    if (passwords.newPassword !== passwords.confirmPassword) return toast.error('新密码与确认密码不匹配'), false;
    if (passwords.newPassword.length < 6) return toast.error('新密码长度至少为 6 个字符'), false;
    if (!/^(?=.*[a-zA-Z])(?=.*\d).+$/.test(passwords.newPassword)) return toast.error('新密码必须包含字母和数字'), false;
    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validatePasswords()) return;
    setIsLoading(true);
    try {
      const success = await updatePassword(passwords.currentPassword, passwords.newPassword);
      if (!success) return toast.error('当前密码错误');
      toast.success('密码已修改，请重新登录');
      setTimeout(async () => {
        try { await logout(); } finally { navigate('/login?logout=true', { replace: true }); }
      }, 1500);
    } catch (error) {
      toast.error(error.message || '修改密码失败');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="page-stack security-page">
      <PageHeader eyebrow="ACCOUNT SECURITY" title="修改密码" description="更新操作员登录凭据，提交后当前会话将安全退出。" icon={KeyRound} badge="安全设置" />
      <div className="security-layout">
        <Card className="password-form-card">
          <CardHeader>
            <CardTitle>登录凭据</CardTitle>
            <CardDescription>请先验证当前密码，再设置新的账户密码。</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit}>
              <FieldGroup>
                <Field>
                  <FieldLabel htmlFor="current-password"><Lock />当前密码</FieldLabel>
                  <Input id="current-password" type="password" value={passwords.currentPassword} onChange={(event) => setPasswords({ ...passwords, currentPassword: event.target.value })} placeholder="输入当前密码" required disabled={isLoading} />
                </Field>
                <Field>
                  <FieldLabel htmlFor="new-password"><KeyRound />新密码</FieldLabel>
                  <Input id="new-password" type="password" value={passwords.newPassword} onChange={(event) => setPasswords({ ...passwords, newPassword: event.target.value })} placeholder="设置新密码" required disabled={isLoading} />
                  <FieldDescription>至少 6 个字符，并同时包含字母和数字。</FieldDescription>
                </Field>
                <Field>
                  <FieldLabel htmlFor="confirm-password"><ShieldCheck />确认新密码</FieldLabel>
                  <Input id="confirm-password" type="password" value={passwords.confirmPassword} onChange={(event) => setPasswords({ ...passwords, confirmPassword: event.target.value })} placeholder="再次输入新密码" required disabled={isLoading} />
                </Field>
                <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
                  <Button type="button" variant="outline" onClick={() => navigate('/')} disabled={isLoading}>取消</Button>
                  <Button type="submit" disabled={isLoading}><KeyRound data-icon="inline-start" />{isLoading ? '正在更新…' : '确认修改'}</Button>
                </div>
              </FieldGroup>
            </form>
          </CardContent>
        </Card>
        <aside className="password-advice" aria-labelledby="password-advice-title">
          <div className="password-advice__header">
            <span className="password-advice__icon" aria-hidden="true"><ShieldCheck /></span>
            <div>
              <span className="password-advice__eyebrow">账户保护</span>
              <h2 id="password-advice-title">创建更可靠的密码</h2>
            </div>
          </div>

          <p className="password-advice__intro">更新前，请确认新密码满足以下安全建议。</p>

          <ul className="password-advice__list">
            <li>
              <span className="password-advice__check" aria-hidden="true"><Check /></span>
              <div><strong>至少 6 个字符</strong><span>避免过短或容易猜测的组合</span></div>
            </li>
            <li>
              <span className="password-advice__check" aria-hidden="true"><Check /></span>
              <div><strong>同时包含字母和数字</strong><span>提高密码组合的复杂程度</span></div>
            </li>
            <li>
              <span className="password-advice__check" aria-hidden="true"><Check /></span>
              <div><strong>不要与其他系统重复</strong><span>降低凭据泄露后的关联风险</span></div>
            </li>
          </ul>

          <div className="password-advice__notice">
            <LogOut aria-hidden="true" />
            <div><strong>会话安全提示</strong><span>修改成功后，当前会话将退出，请使用新密码重新登录。</span></div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default ChangePassword;
